import { Card, CardBody } from '@/components/ui/Card'
import { ROUTES, buildRoute } from '@/constants/routes'
import { Badge, Box, HStack, IconButton, Text, VStack } from '@chakra-ui/react'
import { format } from 'date-fns'
import { FiEdit, FiMail, FiPhone, FiTrash2 } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { LEAD_SOURCE_LABELS } from '../constants/leadEnums'
import { StatusBadge } from './StatusBadge'

/**
 * Lead Card Component
 * Mobile-friendly card view for a single lead
 */
export function LeadCard({ lead, onDelete }) {
  const navigate = useNavigate()

  return (
    <Card>
      <CardBody>
        <VStack align="stretch" spacing={ 3 }>
          {/* Header with status */}
          <HStack justify="space-between">
            <StatusBadge status={ lead.status } />
            <HStack spacing={ 1 }>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Edit lead"
                onClick={ () => navigate(buildRoute(ROUTES.LEADS_EDIT, { uuid: lead.uuid })) }
              >
                <Box as={ FiEdit } />
              </IconButton>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Delete lead"
                onClick={ () => onDelete?.(lead) }
                color="red.500"
                _hover={{ bg: 'red.50', color: 'red.600' }}
              >
                <Box as={ FiTrash2 } />
              </IconButton>
            </HStack>
          </HStack>

          {/* Name and title */}
          <Box>
            <Text fontSize="lg" fontWeight="semibold">
              {lead.first_name} {lead.last_name}
            </Text>
            {lead.title && (
              <Text fontSize="sm" color="text.secondary">
                { lead.title }
              </Text>
            )}
          </Box>

          {/* Contact info */}
          <VStack align="stretch" spacing={ 1 }>
            {lead.phone && (
              <HStack spacing={ 2 } fontSize="sm">
                <Box as={ FiPhone } color="text.secondary" />
                <Text>{ lead.phone }</Text>
              </HStack>
            )}
            {lead.email && (
              <HStack spacing={ 2 } fontSize="sm">
                <Box as={ FiMail } color="text.secondary" />
                <Text>{ lead.email }</Text>
              </HStack>
            )}
          </VStack>

          {/* Source and assigned */}
          <HStack spacing={ 4 } fontSize="sm" color="text.secondary">
            {lead.source && (
              <Text>
                Source: <strong>{ LEAD_SOURCE_LABELS[lead.source] }</strong>
              </Text>
            )}
            {lead.assigned_to && (
              <Text>
                Assigned: <strong>{ lead.assigned_to.username || lead.assigned_to.email }</strong>
              </Text>
            )}
          </HStack>

          {/* Next action */}
          {lead.next_action_due_at && (
            <HStack justify="space-between" fontSize="sm">
              <Text color="text.secondary">Next action:</Text>
              <Badge colorScheme="orange">
                { format(new Date(lead.next_action_due_at), 'dd.MM.yyyy HH:mm') }
              </Badge>
            </HStack>
          )}

          {/* Created date */}
          <Text fontSize="xs" color="text.secondary" textAlign="right">
            Created: { format(new Date(lead.created_at), 'dd.MM.yyyy') }
          </Text>
        </VStack>
      </CardBody>
    </Card>
  )
}

export default LeadCard
